(() => {
  angular
    .module('app')
    .controller('common.views.aceClaims.fullAnalysis', AceClaimFullAnalysisPageController);

  // Attachment page chips shared by every section of the full-analysis view. Each page
  // reference carries ACE's medicalClaimAttachmentName / medicalClaimAttachmentPageNumber
  // pair; chips resolve against the claim's stored attachments and disable when unmatched.
  angular.module('app').component('aceAttachmentPageChips', {
    bindings: {
      pages: '<',
      isAvailable: '<',
      onPreview: '<',
    },
    template: `
      <span class="ace-attachment-chip" ng-repeat="page in $ctrl.pages">
        <button
          type="button"
          class="btn btn-xs btn-default"
          ng-disabled="!$ctrl.isAvailable(page.medicalClaimAttachmentName)"
          uib-tooltip="{{page.chipTooltip || ($ctrl.isAvailable(page.medicalClaimAttachmentName)
            ? '' : $root.l('AttachmentNotFoundInClaim'))}}"
          tooltip-append-to-body="true"
          ng-click="$ctrl.onPreview(page)">
          <i class="far fa-file-alt"></i>&nbsp;{{$root.l('XPageY',
            page.medicalClaimAttachmentName || '—', page.medicalClaimAttachmentPageNumber || '—')}}
        </button>
      </span>`,
  });

  AceClaimFullAnalysisPageController.$inject = [
    '$sce',
    '$stateParams',
    'abp.services.app.aceClaims',
  ];

  function AceClaimFullAnalysisPageController($sce, $stateParams, aceClaimsSvc) {
    const vm = this;

    // The §11 checklist rows shown in the mockup — each row is "available" when any analysed
    // page carries the matching page type or tag.
    const checklistDefinitions = [
      { labelKey: 'ReceiptInvoiceBill', types: ['Bill', 'ProformaBill', 'ProformaInvoice'], tags: [] },
      { labelKey: 'ItemizedBillBreakdown', types: [], tags: ['ItemizedBillBreakdownAvailable'] },
      { labelKey: 'ClaimForm', types: ['ClaimForm'], tags: [] },
      { labelKey: 'DrugPrescription', types: [], tags: ['DrugPrescriptionAvailable'] },
      { labelKey: 'LaboratoryTestResults', types: ['LaboratoryTests'], tags: ['LabResultsAvailable'] },
      { labelKey: 'ImagingReport', types: ['Imaging'], tags: [] },
      { labelKey: 'DischargeSummary', types: [], tags: ['DischargeSummary'] },
    ];

    // Fixed display order of the §11 clinicalDetailsInformation nodes; each node is nullable.
    const clinicalNodeDefinitions = [
      { key: 'symptomsAndConditions', labelKey: 'SymptomsAndConditions' },
      { key: 'natureOfIllness', labelKey: 'NatureOfIllness' },
      { key: 'patientMedicalHistory', labelKey: 'PatientMedicalHistory' },
      { key: 'dateOfFirstSymptom', labelKey: 'DateOfFirstSymptom' },
      { key: 'dateOfFirstConsultation', labelKey: 'DateOfFirstConsultation' },
      { key: 'physicalExamFindings', labelKey: 'PhysicalExamFindings' },
      { key: 'treatmentPlan', labelKey: 'TreatmentPlan' },
      { key: 'admissionDate', labelKey: 'AdmissionDate' },
      { key: 'dischargeDate', labelKey: 'DischargeDate' },
      { key: 'lengthOfStay', labelKey: 'LengthOfStay' },
    ];

    const outcomeStyles = {
      Pass: { labelKey: 'OutcomePass', cssClass: 'label-success' },
      Fail: { labelKey: 'OutcomeFail', cssClass: 'label-danger' },
      RequestInfo: { labelKey: 'OutcomeRequestInfo', cssClass: 'label-warning' },
      Skip: { labelKey: 'OutcomeSkip', cssClass: 'label-default' },
    };

    vm.loading = 0;
    vm.claimNumber = $stateParams.claimNumber;
    vm.loadFailed = false;
    vm.parseFailed = false;
    vm.analysis = null;
    vm.checklist = [];
    vm.checklistAvailableCount = 0;
    vm.outcome = null;
    vm.medical = null;
    vm.medicalCount = 0;
    vm.billing = null;
    vm.billingCount = 0;
    vm.preview = null;

    vm.isAttachmentAvailable = isAttachmentAvailable;
    vm.previewAttachment = previewAttachment;
    vm.closePreview = closePreview;
    vm.outcomeStyle = outcomeStyle;
    vm.checkTypeLabel = checkTypeLabel;
    vm.dash = dash;
    vm.formatConfidence = formatConfidence;

    let attachmentsByName = {};

    init();

    function init() {
      loadAnalysis();
      loadClaimAttachments();
    }

    function loadAnalysis() {
      vm.loading += 1;
      aceClaimsSvc
        .getStoredFullClaimDetails({ claimNumber: vm.claimNumber })
        .success((data) => {
          parseAnalysis(data.detailsJson);
        })
        .error(() => {
          vm.loadFailed = true;
        })
        .finally(() => {
          vm.loading -= 1;
        });
    }

    // The stored §11 payload is raw JSON from ACE — parsed only here, on the client, and
    // rendered defensively: any missing/null part becomes an em-dash or an empty note, and
    // a payload the section builders cannot digest downgrades to the unreadable alert
    // rather than a half-rendered page.
    function parseAnalysis(rawJson) {
      try {
        const analysis = JSON.parse(rawJson);
        if (!analysis || typeof analysis !== 'object') {
          vm.parseFailed = true;
          return;
        }

        buildChecklist(asObjects(analysis.imageMetadata));
        buildOutcome(analysis.outcomeRecommendation);
        buildMedical(analysis.medicalAnalysis);
        buildBilling(analysis.billingAnalysis);
        vm.analysis = analysis;
      } catch (e) {
        vm.parseFailed = true;
        vm.analysis = null;
      }
    }

    // Nullable §11 arrays can also carry null/garbage elements; only real objects render.
    function asObjects(list) {
      return _.filter(_.isArray(list) ? list : [], _.isObject);
    }

    function loadClaimAttachments() {
      vm.loading += 1;
      aceClaimsSvc
        .getClaimAttachments({ claimNumber: vm.claimNumber })
        .success((data) => {
          attachmentsByName = buildAttachmentsByName(data.items || []);
        })
        .error(() => {
          // Chips simply stay disabled when the claim's attachments can't be resolved —
          // attachment lookup must never block the analysis rendering.
          attachmentsByName = {};
        })
        .finally(() => {
          vm.loading -= 1;
        });
    }

    // Chips match on the name ACE was given at registration, so the dictionary must only hold
    // names that can identify one document. A blank fileName (never captured at upload) can't
    // identify anything — keying it would let a null-cited chip "match" an arbitrary document.
    // A name shared by two documents is just as ambiguous, so duplicates are dropped too and
    // their chips stay disabled rather than previewing whichever document happened to win.
    function buildAttachmentsByName(items) {
      const named = _.filter(items, (a) => normaliseName(a.fileName));
      const grouped = _.groupBy(named, (a) => normaliseName(a.fileName));
      return _.mapValues(_.pickBy(grouped, (g) => g.length === 1), (g) => g[0]);
    }

    function buildChecklist(imageMetadata) {
      vm.checklist = _.map(checklistDefinitions, (definition) => {
        const matches = _.filter(imageMetadata, (page) =>
          _.includes(definition.types, page.type) ||
          _.some(_.isArray(page.tag) ? page.tag : [], (t) => _.includes(definition.tags, t))
        );

        return {
          labelKey: definition.labelKey,
          available: matches.length > 0,
          pages: _.map(matches, decorateChecklistPage),
        };
      });
      vm.checklistAvailableCount = _.filter(vm.checklist, 'available').length;
    }

    function decorateChecklistPage(page) {
      const parts = [];
      if (page.type) parts.push(`${App.localize('Type')}: ${page.type}`);
      if (_.isArray(page.tag) && page.tag.length) {
        parts.push(`${App.localize('Tags')}: ${page.tag.join(', ')}`);
      }
      if (page.countryCode) parts.push(`${App.localize('Country')}: ${page.countryCode}`);

      return angular.extend({}, page, { chipTooltip: parts.join(' · ') });
    }

    function buildOutcome(outcomeRecommendation) {
      if (!outcomeRecommendation) {
        vm.outcome = null;
        return;
      }

      vm.outcome = {
        overallOutcome: outcomeRecommendation.overallOutcome,
        overallDescription: outcomeRecommendation.overallDescription,
        checks: _.map(asObjects(outcomeRecommendation.checks), (check) =>
          angular.extend({}, check, { expanded: false })
        ),
      };
    }

    function buildMedical(medicalAnalysis) {
      if (!medicalAnalysis) {
        vm.medical = null;
        vm.medicalCount = 0;
        return;
      }

      const clinicalNodes = [];
      const clinicalDetails = medicalAnalysis.clinicalDetailsInformation;
      if (clinicalDetails) {
        _.forEach(clinicalNodeDefinitions, (definition) => {
          const node = clinicalDetails[definition.key];
          if (_.isObject(node)) {
            clinicalNodes.push({
              labelKey: definition.labelKey,
              value: node.value,
              confidence: node.confidence,
              pages: asObjects(node.attachments),
            });
          }
        });
      }

      vm.medical = {
        diagnoses: asObjects(medicalAnalysis.diagnoses),
        doctors: asObjects(medicalAnalysis.doctors),
        clinicalNodes,
        procedures: asObjects(medicalAnalysis.procedures),
        diagnosticTests: asObjects(medicalAnalysis.diagnosticTests),
      };

      vm.medicalCount =
        vm.medical.diagnoses.length +
        vm.medical.doctors.length +
        clinicalNodes.length +
        vm.medical.procedures.length +
        vm.medical.diagnosticTests.length;
    }

    function buildBilling(billingAnalysis) {
      if (!billingAnalysis) {
        vm.billing = null;
        vm.billingCount = 0;
        return;
      }

      vm.billing = {
        bills: _.map(asObjects(billingAnalysis.bills), (bill) =>
          angular.extend({}, bill, {
            unresolvedWarnings: _.filter(asObjects(bill.warning), (w) => !w.isResolved),
            relatedAttachments: asObjects(bill.relatedAttachments),
            omittedAttachments: asObjects(bill.omittedAttachments),
            financialLines: asObjects(bill.financialLines),
            items: _.map(asObjects(bill.items), (item) =>
              angular.extend({}, item, { expanded: false, childItems: asObjects(item.childItems) })
            ),
          })
        ),
      };
      vm.billingCount = vm.billing.bills.length;
    }

    // ACE echoes the portal's own attachment names back, but defensively: match
    // case-insensitively and ignore stray whitespace.
    function normaliseName(name) {
      return (name || '').trim().toLowerCase();
    }

    function isAttachmentAvailable(attachmentName) {
      return !!attachmentsByName[normaliseName(attachmentName)];
    }

    function previewAttachment(page) {
      const attachment = attachmentsByName[normaliseName(page.medicalClaimAttachmentName)];
      if (!attachment) return;

      const isPdf = attachment.contentType === 'application/pdf';
      const pageNumber = page.medicalClaimAttachmentPageNumber || 1;

      vm.preview = {
        name: attachment.fileName,
        pageNumber,
        isImage: attachment.isImage,
        downloadUrl: attachment.fullUrl,
        // Browsers' built-in PDF viewer honours #page=N; images ignore the fragment.
        frameUrl: $sce.trustAsResourceUrl(
          isPdf ? `${attachment.fullUrl}#page=${pageNumber}` : attachment.fullUrl
        ),
      };
    }

    function closePreview() {
      vm.preview = null;
    }

    function outcomeStyle(outcome) {
      return outcomeStyles[outcome] || { labelKey: null, cssClass: 'label-default' };
    }

    function checkTypeLabel(type) {
      if (!type) return dash(null);
      const key = `AceCheckType_${type}`;
      const localized = App.localize(key);
      return localized === key ? type : localized;
    }

    // Null-safe display: ACE's schema allows null on most leaf fields; the view shows an
    // em-dash instead of blank/broken output. 0 and false are real values and pass through.
    function dash(value) {
      return value === null || value === undefined || value === '' ? '—' : value;
    }

    function formatConfidence(confidence) {
      if (confidence === null || confidence === undefined) return '—';
      return `${Math.round(confidence * 100)}%`;
    }
  }
})();
